import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ProtegoLogo, ProtegoIcon } from '@/components/brand/ProtegoLogo';
import { ScrollingSignalCards } from '@/components/brand/ScrollingSignalCards';
import { followAuthRedirect, isAllowedExternalRedirect, rememberAuthRedirect, resolveAuthRedirect, redirectToExternalAuth, authPathWithRedirect } from '@/lib/auth-redirect';
import { userNeedsSubscription } from '@/lib/plan-access';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfterSignup = searchParams.get('redirect');

  useEffect(() => {
    if (redirectAfterSignup) {
      rememberAuthRedirect(redirectAfterSignup);
    }
  }, [redirectAfterSignup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error('Please fill in all fields'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await signup(name, email, password);
      const target = resolveAuthRedirect(redirectAfterSignup);
      if (isAllowedExternalRedirect(target)) {
        await redirectToExternalAuth(target);
        return;
      }
      const cachedUser = JSON.parse(localStorage.getItem('protego_user') || 'null');
      if (userNeedsSubscription(cachedUser)) {
        toast.success('Account created! Choose a plan to get started.');
        navigate('/settings#plans', { replace: true });
        return;
      }
      toast.success('Account created successfully!');
      followAuthRedirect(redirectAfterSignup, navigate, cachedUser);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Preview */}
      <div className="hidden lg:flex lg:flex-1 bg-card relative overflow-hidden flex-col items-center justify-center">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(900px 360px at 15% 10%, hsl(188 100% 55% / 0.08) 0%, transparent 60%), radial-gradient(900px 360px at 85% 0%, hsl(264 92% 70% / 0.06) 0%, transparent 60%)' }} />
        <div className="relative z-10 w-full max-w-xl px-8 flex flex-col items-center">
          <ProtegoIcon size="xl" className="mb-6" />
          <h2 className="text-2xl font-semibold mb-2 text-center">Protego Enforcement Risk Score</h2>
          <p className="text-sm text-muted-foreground text-center mb-8 max-w-sm">
            Explainable 0-100 risk scoring from behavioral signals, with alerts only when changes matter
          </p>
          <div className="w-full max-w-md bg-background/40 rounded-xl border border-border/30 p-5 backdrop-blur mb-8">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-muted-foreground">Enforcement Risk Score</span>
              <span className="text-xs text-muted-foreground">Top Risk Drivers</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold grad-accent-text tabular-nums">28</span>
              <span className="text-lg text-muted-foreground font-medium">/100</span>
            </div>
            <div className="mt-3 h-1.5 w-full bg-muted/40 rounded-full overflow-hidden">
              <div className="h-full w-[28%] rounded-full" style={{ background: 'linear-gradient(90deg, hsl(142 72% 45%) 0%, hsl(188 100% 55%) 100%)' }} />
            </div>
          </div>
          <ScrollingSignalCards />
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <ProtegoLogo size="md" className="mb-8" />

          <h1 className="text-2xl font-semibold mb-1">Welcome to Protego</h1>
          <p className="text-sm text-muted-foreground mb-8">Sign up to protect your business and stay on top of your numbers.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Name</Label>
              <Input id="name" placeholder="Enter your full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-form-name-input" className="bg-background/40 border-border h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
              <Input id="email" type="email" placeholder="abc@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-form-email-input" className="bg-background/40 border-border h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input id="password" type="password" placeholder="Enter new password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-form-password-input" className="bg-background/40 border-border h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-sm font-medium">Confirm Password</Label>
              <Input id="confirm" type="password" placeholder="Re-type your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="signup-form-confirm-input" className="bg-background/40 border-border h-11" />
            </div>
            <Button type="submit" disabled={loading} data-testid="signup-form-submit-button" className="w-full h-11 text-sm font-medium grad-accent text-white border-0 hover:opacity-90 active:scale-[0.98] transition-opacity">
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to={authPathWithRedirect('/login', redirectAfterSignup)} className="text-[hsl(188,100%,55%)] hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
